<template>
  <div>
    <!-- OBS 버튼(클릭=링크 다이얼로그) + 우상단 카운트 배지(클릭=경로별 연결 상세) -->
    <div class="obs-btn-wrap">
      <v-btn @click="onClickOBS" class="elevation-4" :style="{ width: '40px', height: '40px' }" icon>
        <v-badge bordered color="error" :content="`${liveModelCount}`" overlap>
          <div style="border-radius: 4px; overflow: hidden">
            <v-obs-prism-icon :size="40"></v-obs-prism-icon>
          </div>
        </v-badge>
      </v-btn>

      <!-- 배지(모양·색·폰트 = v-badge 그대로) 위 투명 클릭영역 → 경로별 연결 분포 메뉴 -->
      <v-menu top right offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <span class="obs-count-hit" v-on="on" v-bind="attrs" @click.stop title="연결 현황 보기"></span>
        </template>
        <v-card class="pa-3" min-width="220">
          <div class="text-caption font-weight-bold mb-2">오버레이 연결 현황 (경로별)</div>
          <div
            v-for="(c, path) in breakdown"
            :key="path"
            class="d-flex justify-space-between align-center"
            style="gap: 16px; font-size: 13px; line-height: 1.7"
          >
            <span class="grey--text text--darken-2">{{ path }}</span>
            <span class="font-weight-bold">{{ c }}</span>
          </div>
          <div v-if="!hasBreakdown" class="text-caption grey--text">연결된 오버레이가 없습니다.</div>
        </v-card>
      </v-menu>
    </div>

    <v-dialog v-model="isShowDialog" width="560" content-class="obs-dialog-shell">
      <v-card class="obs-dialog">
        <!-- 헤더 -->
        <div class="obs-dialog__head">
          <div class="obs-dialog__glyph"><v-icon color="#fff" size="22">mdi-broadcast</v-icon></div>
          <div class="obs-dialog__heads">
            <div class="obs-dialog__title">OBS 송출 링크</div>
            <div class="obs-dialog__sub">OBS·PRISM의 브라우저 소스에 붙여넣으면 펫이 방송 화면에 나와요.</div>
          </div>
          <div v-if="streamerAliasId" class="obs-chip">
            <v-icon size="13" class="mr-1">mdi-account</v-icon>{{ streamerAliasId }}
          </div>
        </div>

        <!-- 본문 -->
        <div class="obs-dialog__body">
          <!-- 스트리머 미설정 안내 -->
          <div v-if="!streamerAliasId" class="obs-alert">
            <v-icon size="16" color="#d4456a" class="mr-1">mdi-alert-circle-outline</v-icon>
            스트리머 이름을 설정해야 채팅에 반응해요.
          </div>

          <div class="obs-eyebrow">스트림 키 <span>· 외부 공유 금지</span></div>

          <!-- 시그니처: 스트림 키 크리덴셜 카드 -->
          <div class="keycard">
            <v-icon class="keycard__lock" size="17">
              {{ revealed ? "mdi-lock-open-variant-outline" : "mdi-lock-outline" }}
            </v-icon>
            <div class="keycard__value" :class="{ masked: !revealed }">
              <template v-if="isLoading">발급 중…</template>
              <template v-else>{{ revealed ? obsLink : maskedLink }}</template>
            </div>
            <button
              class="keycard__btn"
              type="button"
              :disabled="isLoading"
              :title="revealed ? '가리기' : '보기'"
              @click="onToggleReveal"
            >
              <v-icon size="17" color="#8e84b0">{{ revealed ? "mdi-eye-off-outline" : "mdi-eye-outline" }}</v-icon>
            </button>
            <button class="keycard__btn" type="button" :disabled="isLoading" title="복사" @click="onClickCopy">
              <v-icon size="17" :color="isCopied ? '#2ea36f' : '#8e84b0'">
                {{ isCopied ? "mdi-check" : "mdi-content-copy" }}
              </v-icon>
            </button>
          </div>

          <div class="obs-sec">
            <v-icon size="15" color="#d4456a" class="mr-1">mdi-shield-alert-outline</v-icon>
            방송 중 노출 금지 · 유출되면 바로 재발급하세요.
          </div>

          <div class="obs-help">
            <v-icon size="15" color="#8b80b8" class="mr-1">mdi-information-outline</v-icon>
            OBS › 소스 추가 › <b class="ml-1 mr-1">브라우저</b> › URL에 붙여넣기
          </div>

          <div class="obs-meta">
            <span class="obs-spec">권장 크기 400 × 400</span>
            <button v-if="isLocalEnv" class="obs-devlink" type="button" :disabled="isLoading" @click="onClickCopyDev">
              <v-icon size="14" class="mr-1">mdi-flask-outline</v-icon>DEV 링크 복사
            </button>
          </div>
        </div>

        <!-- 액션 -->
        <div class="obs-dialog__foot">
          <v-btn text color="#d4456a" :loading="isLoading" @click="onClickRegenerate">
            <v-icon left size="18">mdi-refresh</v-icon>재발급
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="#7C4DFF" class="white--text px-6" @click="isShowDialog = false">닫기</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- 스트림 키 표시 안내 (보기 전 1회 확인) -->
    <v-dialog v-model="showRevealWarning" width="430" persistent content-class="obs-dialog-shell">
      <v-card class="obs-warn">
        <div class="obs-warn__head">
          <div class="obs-warn__glyph"><v-icon color="#fff" size="20">mdi-shield-alert-outline</v-icon></div>
          <div class="obs-warn__title">스트림 키를 표시할까요?</div>
          <v-btn icon small @click="showRevealWarning = false"><v-icon size="20">mdi-close</v-icon></v-btn>
        </div>
        <div class="obs-warn__body">
          <p class="obs-warn__lead">방송 중 노출되거나 다른 사람과 공유하면 안 돼요.</p>
          <p class="obs-warn__desc mb-0">
            키가 노출되면 다른 사람이 내 오버레이를 열어 채팅을 가로챌 수 있어요. 이해했다면 ‘스트림 키 보기’를
            눌러주세요.
          </p>
        </div>
        <div class="obs-warn__foot">
          <v-btn text @click="showRevealWarning = false">취소</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="#d4456a" class="white--text" @click="confirmReveal">
            <v-icon left size="18">mdi-eye-outline</v-icon>스트림 키 보기
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isLocal } from "@/utils/env-utils";
import copyToClipboard from "@/utils/copyToClipboard";

export default {
  props: {
    liveModelCount: {
      type: String,
      default: "-",
    },
    // 경로별 연결 분포 { "/on-air": 1, "/on-air/dev": 2, ... } — 배지 클릭 시 상세 표시
    breakdown: {
      type: Object,
      default: () => ({}),
    },
    email: {
      type: String,
      default: null,
    },
    streamerAliasId: {
      type: String,
      default: null,
    },
  },
  computed: {
    hasBreakdown() {
      return Object.keys(this.breakdown || {}).length > 0;
    },
    // 마스킹 표시용 점열 (실제 토큰은 노출하지 않음)
    maskedLink() {
      return "•".repeat(30);
    },
  },

  created() {},
  data() {
    return {
      isShowDialog: false,
      isCopied: false,
      isLoading: false,
      obsLink: null,
      obsDevLink: null,
      isLocalEnv: isLocal(), // 로컬에서만 dev 링크 노출
      revealed: false, // 스트림 키 표시 여부 (기본 마스킹)
      confirmedReveal: false, // 경고 1회 확인 여부 (다이얼로그 열 때마다 리셋)
      showRevealWarning: false,
    };
  },

  methods: {
    onClose() {
      this.isShowDialog = false;
    },
    // OBS 링크 버튼: 서버에 토큰 보장(ensure) — 있으면 그대로, 없으면 생성. 여러 번 눌러도 같은 링크.
    async onClickOBS() {
      if (!this.email) {
        return this.$notify({ type: "warning", title: "알림", text: "관리자에게 문의해주세요", duration: 1500 });
      }
      this.isShowDialog = true;
      this.revealed = false; // 열 때마다 마스킹 + 재확인
      this.confirmedReveal = false;
      await this._loadLink("ensure");
    },
    // 눈 토글: 가려진 상태에서 보려면 경고 1회 확인 필요.
    onToggleReveal() {
      if (this.revealed) {
        this.revealed = false;
        return;
      }
      if (!this.confirmedReveal) {
        this.showRevealWarning = true;
        return;
      }
      this.revealed = true;
    },
    confirmReveal() {
      this.confirmedReveal = true;
      this.revealed = true;
      this.showRevealWarning = false;
    },
    // 재발급: 회전(기존 OBS 링크 즉시 무효화)
    async onClickRegenerate() {
      if (!window.confirm("기존에 OBS에 등록한 링크가 즉시 무효화됩니다. 새 링크를 발급할까요?")) return;
      await this._loadLink("regenerate");
    },
    async _loadLink(mode) {
      try {
        this.isLoading = true;
        // 스토어 액션이 캐싱 담당 → ensure는 첫 1회만 서버 호출, 이후 캐시 반환
        const token =
          mode === "regenerate"
            ? await this.$store.dispatch("broadcast/regenerateToken")
            : await this.$store.dispatch("broadcast/ensureToken");
        this.obsLink = `${window.location.origin}/on-air?t=${token}`;
        this.obsDevLink = `${window.location.origin}/on-air/dev?t=${token}`;
      } catch (error) {
        console.error(error);
        this.$notify({ type: "error", title: "토큰 발급 실패", text: "잠시 후 다시 시도해주세요", duration: 1500 });
      } finally {
        this.isLoading = false;
      }
    },
    // 복사는 마스킹 여부와 무관하게 전체 URL 복사 (화면 노출 없이)
    async onClickCopy() {
      await this._copyClipboard(this.obsLink);
    },
    onClickCopyDev() {
      this._copyClipboard(this.obsDevLink);
    },

    async _copyClipboard(text) {
      const ok = await copyToClipboard(text);
      if (ok) {
        this.isCopied = true;
        this.$notify({ type: "success", title: "복사됨", duration: 1000 });
        setTimeout(() => {
          this.isCopied = false;
        }, 1000);
      } else {
        this.$notify({ type: "error", title: "복사 실패", text: "키를 길게 눌러 직접 복사해 주세요.", duration: 2000 });
      }
    },
  },
};
</script>

<style scoped>
/* ===== OBS 버튼 + 카운트 배지 (다이얼로그 트리거) ===== */
.obs-btn-wrap {
  position: relative;
  display: inline-block;
}
.obs-count-hit {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
}

/* ===== 메인 다이얼로그 ===== */
.obs-dialog {
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(34, 22, 60, 0.28);
}
.obs-dialog__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 22px;
  background: linear-gradient(135deg, #f3efff 0%, #ece3ff 100%);
  border-bottom: 1px solid #e7e1f7;
}
.obs-dialog__glyph {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7c4dff, #6a3df0);
  box-shadow: 0 8px 18px rgba(124, 77, 255, 0.4);
}
.obs-dialog__heads {
  min-width: 0;
  flex: 1 1 auto;
}
.obs-dialog__title {
  font-family: maplestory;
  font-size: 19px;
  color: #221c3a;
  line-height: 1.25;
}
.obs-dialog__sub {
  font-size: 12.5px;
  color: #6f659a;
  margin-top: 4px;
  line-height: 1.45;
}
.obs-chip {
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  max-width: 130px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #ddd1f7;
  color: #5b3fd6;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.obs-chip >>> .v-icon {
  color: #7c4dff;
}

.obs-dialog__body {
  padding: 18px 22px 20px;
}
.obs-alert {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  margin-bottom: 14px;
  background: #fff0f3;
  border: 1px solid #ffd3dd;
  border-radius: 10px;
  color: #c23a5b;
  font-size: 13px;
  font-weight: 600;
}
.obs-eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #6f659a;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.obs-eyebrow span {
  color: #b6abd6;
  font-weight: 700;
}

/* ===== 스트림 키 필드 (밝은 중립 톤) ===== */
.keycard {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 14px;
  border-radius: 12px;
  background: #f5f3fa;
  border: 1px solid #e5e0f0;
}
.keycard__lock {
  color: #a59cc4 !important;
  flex: 0 0 auto;
}
.keycard__value {
  flex: 1 1 auto;
  min-width: 0;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  color: #423b5e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
}
.keycard__value.masked {
  color: #aaa1c4;
  letter-spacing: 0.2em;
}
.keycard__btn {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
}
.keycard__btn:hover {
  background: #ebe6f6;
}
.keycard__btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.obs-sec {
  display: flex;
  align-items: center;
  margin-top: 11px;
  font-size: 12.5px;
  font-weight: 700;
  color: #d4456a;
}
.obs-help {
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 12.5px;
  color: #6f659a;
}
.obs-help b {
  color: #5b3fd6;
}
.obs-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
}
.obs-spec {
  font-size: 12px;
  font-weight: 700;
  color: #6f659a;
  padding: 5px 11px;
  border-radius: 999px;
  background: #f4f1fc;
  border: 1px solid #e7e1f7;
}
.obs-devlink {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #8b80b8;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: color 0.15s ease, background 0.15s ease;
}
.obs-devlink:hover {
  color: #5b3fd6;
  background: #f4f1fc;
}
.obs-devlink >>> .v-icon {
  color: currentColor;
}
.obs-dialog__foot {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  border-top: 1px solid #eee8f8;
  background: #fbfaff;
}

/* ===== reveal 경고 다이얼로그 ===== */
.obs-warn {
  border-radius: 18px;
}
.obs-warn__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 12px;
}
.obs-warn__glyph {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ff5c7a, #e23a5e);
  box-shadow: 0 6px 14px rgba(226, 58, 94, 0.35);
}
.obs-warn__title {
  flex: 1 1 auto;
  font-family: maplestory;
  font-size: 17px;
  color: #221c3a;
}
.obs-warn__body {
  padding: 0 18px 6px;
}
.obs-warn__lead {
  font-weight: 800;
  color: #c23a5b;
  font-size: 14px;
  margin-bottom: 8px;
}
.obs-warn__desc {
  font-size: 13px;
  color: #5c5575;
  line-height: 1.55;
}
.obs-warn__foot {
  display: flex;
  align-items: center;
  padding: 12px 16px 16px;
}
</style>
